import React, { useMemo, useState, useEffect } from "react";
import { PAGINATION_PAGE_SIZE_OPTIONS } from "../../config/pagination";
import { displayDate } from "../../utils/dateUtils";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Avatar,
  AvatarGroup,
  Grid,
  Paper,
  Tab,
  Tabs,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  Tooltip,
  Skeleton,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Business as DepartmentIcon,
  People as PeopleIcon,
  School as SchoolIcon,
  MenuBook as SubjectIcon,
  Person as PersonIcon,
  TrendingUp as TrendingIcon,
  CalendarMonth as CalendarIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Download as DownloadIcon,
  BarChart as ChartIcon,
  Add as AddIcon,
  Block as BlockIcon,
} from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useDepartmentViewDetails } from "../../hooks/queries/useDepartmentViewDetails";
import { queryKeys } from "../../hooks/queries/queryKeys";
import courseService from "../../services/courseService";
import api from "../../services/api";

const EMPTY_DEPARTMENT_DATA = {
  id: null,
  name: "",
  code: "",
  establishedYear: null,
  description: "",
  hodName: "",
  hodEmail: "",
  hodPhone: "",
  hodAvatar: "",
  hodDesignation: "",
  office: "",
  phone: "",
  email: "",
  website: "",
  vision: "",
  mission: "",
};

const DEFAULT_DEPARTMENT_STATS = {
  totalFaculty: 0,
  totalStudents: 0,
  totalCourses: 0,
  totalSubjects: 0,
};

const getInitials = (value = "") => {
  const trimmed = value.trim();
  if (!trimmed) {
    return "NA";
  }
  return trimmed
    .split(/\s+/)
    .map((part) => part.charAt(0).toUpperCase())
    .join("")
    .slice(0, 2);
};

const toNumber = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const pickDefined = (sources, fallback) => {
  for (const [obj, key] of sources) {
    if (obj && Object.prototype.hasOwnProperty.call(obj, key)) {
      return obj[key];
    }
  }
  return fallback;
};

const withFallback = (value, fallback = "-") => {
  if (value === undefined || value === null || value === "") {
    return fallback;
  }
  return value;
};

const buildDepartmentData = (departmentDetails, payload) => {
  if (!departmentDetails || typeof departmentDetails !== "object") {
    return { ...EMPTY_DEPARTMENT_DATA };
  }

  const hodDetails =
    departmentDetails?.hod ||
    departmentDetails?.headOfDepartment ||
    payload?.hod ||
    {};

  const resolvedCode = pickDefined(
    [
      [departmentDetails, "code"],
      [departmentDetails, "departmentCode"],
      [payload, "code"],
    ],
    ""
  );

  const hodName =
    departmentDetails?.hodName ||
    hodDetails?.fullName ||
    hodDetails?.name ||
    "";

  return {
    ...EMPTY_DEPARTMENT_DATA,
    id: departmentDetails?.id ?? departmentDetails?._id ?? null,
    name: departmentDetails?.name ?? departmentDetails?.departmentName ?? "",
    code: resolvedCode,
    establishedYear:
      departmentDetails?.establishedYear ?? departmentDetails?.established ?? null,
    description: departmentDetails?.description ?? "",
    hodName,
    hodEmail:
      departmentDetails?.hodEmail ??
      hodDetails?.email ??
      "",
    hodPhone:
      departmentDetails?.hodPhone ??
      hodDetails?.phoneNumber ??
      hodDetails?.mobileNumber ??
      hodDetails?.phone ??
      hodDetails?.contactNumber ??
      "",
    hodDesignation:
      departmentDetails?.hodDesignation ?? hodDetails?.designation ?? "",
    hodAvatar:
      departmentDetails?.hodAvatar ??
      hodDetails?.avatar ??
      getInitials(hodName),
    office:
      departmentDetails?.office ??
      departmentDetails?.location ??
      departmentDetails?.address ??
      "",
    phone:
      departmentDetails?.phone ?? departmentDetails?.contactNumber ?? "",
    email: departmentDetails?.email ?? "",
    website: departmentDetails?.website ?? "",
    vision: departmentDetails?.vision ?? "",
    mission: departmentDetails?.mission ?? "",
  };
};

const buildDepartmentStats = (statsPayload, payload) => {
  const base = { ...DEFAULT_DEPARTMENT_STATS };

  const stats =
    (statsPayload && typeof statsPayload === "object" && statsPayload) ||
    {};

  const numberFields = {
    totalFaculty: [
      stats?.totalFaculty,
      stats?.facultyCount,
      payload?.teacherCount,
    ],
    totalStudents: [
      stats?.totalStudents,
      stats?.studentCount,
      payload?.studentCount,
    ],
    totalCourses: [stats?.totalCourses, stats?.courseCount, payload?.courseCount],
    totalSubjects: [stats?.totalSubjects, stats?.subjectCount, payload?.subjectCount],
    placementRate: [
      stats?.placementRate,
      stats?.placementPercentage,
      payload?.placementRate,
    ],
    researchPapers: [
      stats?.researchPapers,
      stats?.researchCount,
      payload?.researchPapers,
    ],
  };

  const textFields = {
    averagePackage: [
      stats?.averagePackage,
      stats?.avgPackage,
      payload?.averagePackage,
    ],
    highestPackage: [
      stats?.highestPackage,
      stats?.maxPackage,
      payload?.highestPackage,
    ],
  };

  const isDefined = (value) => value !== undefined && value !== null && value !== "";

  Object.entries(numberFields).forEach(([key, values]) => {
    const value = values.find((item) => item !== undefined && item !== null);
    if (value !== undefined && value !== null) {
      base[key] = toNumber(value, base[key]);
    }
  });

  Object.entries(textFields).forEach(([key, values]) => {
    const value = values.find(isDefined);
    if (value) {
      base[key] = value;
    }
  });

  return base;
};

const buildFacultyMembers = (list = []) => {
  if (!Array.isArray(list)) {
    return [];
  }

  return list.map((member, index) => {
    const fullName =
      member?.name ||
      [member?.firstName, member?.lastName]
        .filter(Boolean)
        .join(" ");

    return {
      id: member?.id || member?._id || index,
      name: fullName || `Faculty ${index + 1}`,
      designation:
        member?.designation ||
        member?.role ||
        member?.position ||
        "",
      specialization:
        member?.specialization ||
        member?.expertise ||
        member?.primarySubject ||
        member?.subject ||
        "",
      avatar:
        member?.avatar ||
        member?.initials ||
        getInitials(fullName || `Faculty ${index + 1}`),
    };
  });
};

const buildCourses = (list = []) => {
  if (!Array.isArray(list)) {
    return [];
  }

  return list.map((course, index) => ({
    id: course?.id || course?._id || index,
    name:
      course?.name ||
      course?.title ||
      course?.courseName ||
      `Course ${index + 1}`,
    code:
      course?.code || course?.courseCode || course?.programCode || "",
    duration:
      course?.duration ||
      course?.courseDuration ||
      course?.programDuration ||
      "",
    intake: toNumber(
      course?.intake || course?.totalSeats || course?.capacity,
      0
    ),
    enrolled: toNumber(
      course?.enrolled ||
        course?.enrolledCount ||
        course?.currentEnrollment,
      0
    ),
  }));
};

const buildEvents = (list = []) => {
  if (!Array.isArray(list)) {
    return [];
  }

  return list.map((event, index) => ({
    id: event?.id || event?._id || index,
    title: event?.title || event?.name || `Event ${index + 1}`,
    date: event?.date || event?.eventDate || event?.scheduledOn || "",
    type: event?.type || event?.category || "Event",
  }));
};

const buildAchievements = (list = []) => {
  if (!Array.isArray(list)) {
    return [];
  }

  return list.map((item, index) => ({
    id: item?.id || item?._id || index,
    title: item?.title || item?.name || `Achievement ${index + 1}`,
    description: item?.description || item?.details || item?.summary || "",
  }));
};

const DepartmentViewSkeleton = () => (
  <Box>
    <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 3 }}>
      <Skeleton variant="circular" width={80} height={80} />
      <Box sx={{ flex: 1 }}>
        <Skeleton variant="text" width={280} height={44} />
        <Skeleton variant="text" width={160} height={24} sx={{ mt: 0.5 }} />
      </Box>
    </Box>
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {[1, 2, 3, 4].map((i) => (
        <Grid item xs={6} sm={3} key={i}>
          <Card>
            <CardContent sx={{ textAlign: "center", py: 2 }}>
              <Skeleton variant="circular" width={40} height={40} sx={{ mx: "auto" }} />
              <Skeleton variant="text" width={48} height={40} sx={{ mx: "auto", mt: 1 }} />
              <Skeleton variant="text" width={90} height={20} sx={{ mx: "auto" }} />
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Skeleton variant="circular" width={64} height={64} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width={200} height={28} />
            <Skeleton variant="text" width={160} height={20} />
            <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
              <Skeleton variant="text" width={140} height={20} />
              <Skeleton variant="text" width={120} height={20} />
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
    <Card>
      <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2, pt: 1 }}>
        <Box sx={{ display: "flex", gap: 2 }}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} variant="rounded" width={72} height={32} />
          ))}
        </Box>
      </Box>
      <CardContent>
        <Skeleton variant="text" width="100%" height={24} />
        <Skeleton variant="text" width="100%" height={24} sx={{ mt: 1 }} />
        <Skeleton variant="text" width="80%" height={24} sx={{ mt: 1 }} />
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rounded" height={80} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rounded" height={80} />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  </Box>
);

const COURSES_DEFAULT_LIMIT = 10;
const COURSES_SORT = "createdAt";
const COURSES_ORDER = "desc";

const EVENTS_DEFAULT_LIMIT = 10;
const EVENTS_SORT = "id";
const EVENTS_ORDER = "DESC";

const normalizeEventType = (value) => {
  if (!value || typeof value !== "string") return "Cultural";
  const v = value.trim().toLowerCase();
  if (v === "cultural") return "Cultural";
  if (v === "technical") return "Technical";
  if (v === "sports") return "Sports";
  if (v === "academic") return "Academic";
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};

function DepartmentView() {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState("");
  const [coursesPage, setCoursesPage] = useState(0);
  const [coursesRowsPerPage, setCoursesRowsPerPage] = useState(COURSES_DEFAULT_LIMIT);
  const [eventsPage, setEventsPage] = useState(0);
  const [eventsRowsPerPage, setEventsRowsPerPage] = useState(EVENTS_DEFAULT_LIMIT);

  const { selectedInstitute, isInInstituteContext } = useSelector(
    (state) => state.instituteContext || {}
  );
  const [user] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  });
  const userRole = (user?.role || localStorage.getItem("userRole") || "").toLowerCase();
  const normalizedRole = userRole === "institute" ? "admin" : userRole;
  const isSuperAdmin = normalizedRole === "superadmin";
  const canPickDepartment =
    normalizedRole === "admin" || userRole === "institute" || isSuperAdmin;
  const effectiveInstituteId =
    isSuperAdmin && isInInstituteContext
      ? selectedInstitute?.id
      : user?.instituteId ?? user?.institute?.id ?? null;

  const departmentsApiParams = useMemo(() => {
    const params = {};
    if (effectiveInstituteId) params.instituteId = effectiveInstituteId;
    return params;
  }, [effectiveInstituteId]);

  const {
    data: departmentsApiResponse,
    isLoading: departmentsListLoading,
    isError: departmentsListError,
    error: departmentsListErrorObj,
  } = useQuery({
    queryKey: queryKeys.departmentView.departments(effectiveInstituteId),
    queryFn: async () => {
      const res = await api.get("/institute/get-departments-names", {
        params: departmentsApiParams,
      });
      return res?.data;
    },
    enabled: canPickDepartment,
    staleTime: 60 * 1000,
  });

  const departmentsListFromApi = useMemo(() => {
    const raw = departmentsApiResponse?.data;
    if (!Array.isArray(raw)) return [];
    return raw
      .map((item, index) => {
        if (item == null) return null;
        if (typeof item === "string") {
          const name = item.trim();
          if (!name) return null;
          return { id: name, name };
        }
        const name = item?.name ?? item?.departmentName ?? item?.title ?? "";
        const resolvedName = typeof name === "string" ? name.trim() : "";
        if (!resolvedName) return null;
        const id = item?.id ?? item?._id;
        return {
          id: id != null ? String(id) : String(resolvedName),
          name: resolvedName,
        };
      })
      .filter(Boolean);
  }, [departmentsApiResponse]);

  useEffect(() => {
    if (!canPickDepartment || departmentsListFromApi.length === 0) return;
    const exists = departmentsListFromApi.some(
      (d) => String(d.id) === String(selectedDepartmentId)
    );
    if (!selectedDepartmentId || !exists) {
      setSelectedDepartmentId(String(departmentsListFromApi[0].id));
    }
  }, [canPickDepartment, departmentsListFromApi, selectedDepartmentId]);

  const detailsDepartmentId = canPickDepartment ? selectedDepartmentId : undefined;
  const detailsQueryEnabled =
    !canPickDepartment || (selectedDepartmentId !== "" && departmentsListFromApi.length > 0);

  const {
    data: payload,
    isLoading,
    isFetching,
    isError,
    error: detailsError,
  } = useDepartmentViewDetails(detailsDepartmentId, {
    enabled: detailsQueryEnabled,
  });

  const activeDepartmentId = canPickDepartment
    ? selectedDepartmentId
    : payload?.department?.id;

  const coursesApiParams = useMemo(
    () => {
      const params = {
        page: coursesPage + 1,
        limit: coursesRowsPerPage,
        sort: COURSES_SORT,
        order: COURSES_ORDER,
        search: "",
      };
      if (effectiveInstituteId) params.instituteId = effectiveInstituteId;
      if (activeDepartmentId) params.departmentId = activeDepartmentId;
      return params;
    },
    [coursesPage, coursesRowsPerPage, effectiveInstituteId, activeDepartmentId]
  );

  const {
    data: coursesApiResponse,
    isLoading: coursesLoading,
    isError: coursesError,
  } = useQuery({
    queryKey: ["department-view-courses", coursesApiParams],
    queryFn: () => courseService.getCourses(coursesApiParams),
    enabled: tabValue === 1 && detailsQueryEnabled,
    staleTime: 60 * 1000,
  });

  const coursesListFromApi = useMemo(() => {
    if (!coursesApiResponse?.data) return [];
    const root = coursesApiResponse.data?.data ?? coursesApiResponse.data ?? {};
    const raw = Array.isArray(root?.courses)
      ? root.courses
      : Array.isArray(root?.data?.courses)
      ? root.data.courses
      : Array.isArray(root?.data)
      ? root.data
      : Array.isArray(root?.rows)
      ? root.rows
      : Array.isArray(root)
      ? root
      : [];
    return raw.map((c, index) => {
      const dept = c?.department;
      const departmentName =
        typeof dept === "string" ? dept : dept?.name ?? "";
      const duration =
        c?.duration != null
          ? typeof c.duration === "number"
            ? `${c.duration} Year${c.duration !== 1 ? "s" : ""}`
            : String(c.duration)
          : "";
      return {
        id: c?.id ?? c?.uuid ?? index,
        name: c?.name ?? c?.courseName ?? c?.title ?? `Course ${index + 1}`,
        code: c?.code ?? c?.courseCode ?? "",
        duration,
        studentsCount: Number(c?.studentsCount ?? c?.students_count ?? 0),
        subjectsCount: Number(c?.subjectsCount ?? c?.subjects_count ?? 0),
        department: departmentName,
        description: c?.description ?? "",
        status: c?.status,
      };
    });
  }, [coursesApiResponse]);

  const coursesPagination = useMemo(() => {
    if (!coursesApiResponse?.data) return { total: 0, page: 1, limit: COURSES_DEFAULT_LIMIT, totalPages: 0 };
    const root = coursesApiResponse.data?.data ?? coursesApiResponse.data ?? {};
    const p = root?.pagination ?? {};
    return {
      total: Number(p?.total ?? 0),
      page: Number(p?.page ?? 1),
      limit: Number(p?.limit ?? COURSES_DEFAULT_LIMIT),
      totalPages: Number(p?.totalPages ?? 0),
    };
  }, [coursesApiResponse]);

  useEffect(() => {
    if (tabValue !== 1) return;
    const maxPage = Math.max(0, coursesPagination.totalPages - 1);
    if (coursesPage > maxPage) setCoursesPage(maxPage);
  }, [tabValue, coursesPage, coursesPagination.totalPages]);

  useEffect(() => {
    setCoursesPage(0);
    setEventsPage(0);
  }, [activeDepartmentId]);

  const eventsApiParams = useMemo(
    () => ({
      page: eventsPage + 1,
      limit: eventsRowsPerPage,
      sort: EVENTS_SORT,
      order: EVENTS_ORDER,
      ...(effectiveInstituteId != null && (isSuperAdmin || normalizedRole === "admin")
        ? { instituteId: Number(effectiveInstituteId) }
        : {}),
    }),
    [eventsPage, eventsRowsPerPage, effectiveInstituteId, isSuperAdmin, normalizedRole]
  );

  const {
    data: eventsApiResponse,
    isLoading: eventsLoading,
    isError: eventsError,
  } = useQuery({
    queryKey: ["department-view-events", eventsApiParams],
    queryFn: async () => {
      const params = new URLSearchParams(eventsApiParams);
      const res = await api.get(`/academic-info/events/all?${params.toString()}`);
      return res?.data;
    },
    enabled: tabValue === 2 && detailsQueryEnabled,
    staleTime: 60 * 1000,
  });

  const eventsListFromApi = useMemo(() => {
    if (!eventsApiResponse) return [];
    const data = eventsApiResponse?.data ?? eventsApiResponse ?? {};
    const list = data?.events ?? data?.data ?? [];
    const raw = Array.isArray(list) ? list : [];
    return raw.map((r, index) => ({
      id: r?.id ?? r?.uuid ?? index,
      name: r?.name ?? r?.title ?? `Event ${index + 1}`,
      date: r?.startDate ?? r?.date ?? r?.eventDate ?? "",
      type: normalizeEventType(r?.type ?? r?.category),
      location: r?.location ?? r?.venue ?? "—",
    }));
  }, [eventsApiResponse]);

  const eventsPagination = useMemo(() => {
    if (!eventsApiResponse) return { total: 0, page: 1, limit: EVENTS_DEFAULT_LIMIT, totalPages: 0 };
    const data = eventsApiResponse?.data ?? eventsApiResponse ?? {};
    const p = data?.pagination ?? {};
    return {
      total: Number(p?.total ?? 0),
      page: Number(p?.page ?? 1),
      limit: Number(p?.limit ?? EVENTS_DEFAULT_LIMIT),
      totalPages: Math.ceil(Number(p?.total ?? 0) / Number(p?.limit ?? EVENTS_DEFAULT_LIMIT)) || 0,
    };
  }, [eventsApiResponse]);

  useEffect(() => {
    if (tabValue !== 2) return;
    const maxPage = Math.max(0, eventsPagination.totalPages - 1);
    if (eventsPage > maxPage) setEventsPage(maxPage);
  }, [tabValue, eventsPage, eventsPagination.totalPages]);

  const derived = useMemo(() => {
    const emptyResult = {
      departmentData: { ...EMPTY_DEPARTMENT_DATA },
      departmentStats: { ...DEFAULT_DEPARTMENT_STATS },
      facultyMembers: [],
      courses: [],
      upcomingEvents: [],
      achievements: [],
      hasRemoteDepartmentData: false,
    };
    if (isError) {
      return emptyResult;
    }
    if (!payload) return null;
    const departmentDetails =
      payload?.department || payload?.departmentDetails || payload;
    const hasDepartmentPayload =
      departmentDetails &&
      typeof departmentDetails === "object" &&
      Object.values(departmentDetails).some(
        (value) => value !== undefined && value !== null && value !== ""
      );
    const stats =
      payload?.stats ||
      payload?.statistics ||
      payload?.departmentStats ||
      payload?.overview ||
      (payload?.teacherCount != null || payload?.studentCount != null
        ? payload
        : {});
    const facultyList =
      payload?.facultyMembers || payload?.faculty || payload?.staff || [];
    const coursesList = payload?.courses || payload?.programs || [];
    const eventsList =
      payload?.events || payload?.upcomingEvents || payload?.announcements || [];
    const achievementsList =
      payload?.achievements || payload?.recognitions || [];
    const nextFaculty = buildFacultyMembers(facultyList);
    const nextCourses = buildCourses(coursesList);
    const nextEvents = buildEvents(eventsList);
    const nextAchievements = buildAchievements(achievementsList);
    return {
      departmentData: hasDepartmentPayload
        ? buildDepartmentData(departmentDetails, payload)
        : { ...EMPTY_DEPARTMENT_DATA },
      departmentStats: buildDepartmentStats(stats, payload),
      facultyMembers: nextFaculty,
      courses: nextCourses,
      upcomingEvents: nextEvents,
      achievements: nextAchievements,
      hasRemoteDepartmentData: Boolean(hasDepartmentPayload),
    };
  }, [payload, isError]);

  const waitingForDepartmentPick =
    canPickDepartment &&
    (departmentsListLoading ||
      (!departmentsListError && departmentsListFromApi.length > 0 && !selectedDepartmentId));

  const activeError = departmentsListErrorObj || detailsError;
  const isForbidden =
    Boolean(
      activeError?.response?.status === 403 ||
      activeError?.response?.status === 401 ||
      /unauthorized/i.test(activeError?.response?.data?.message || "") ||
      /permission/i.test(activeError?.response?.data?.message || "")
    );

  if (isForbidden) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 400,
          gap: 2,
          p: 3,
          textAlign: "center",
        }}
      >
        <BlockIcon sx={{ fontSize: 80, color: "error.main" }} />
        <Typography variant="h5" fontWeight={600} color="text.primary">
          Access Denied
        </Typography>
        <Typography variant="body1" color="text.secondary" maxWidth={500}>
          {activeError?.response?.data?.message ||
            "You do not have permission to view department information. Please contact your administrator."}
        </Typography>
        <Button variant="outlined" onClick={() => navigate(-1)} sx={{ mt: 1 }}>
          Go Back
        </Button>
      </Box>
    );
  }

  if (
    canPickDepartment &&
    !departmentsListLoading &&
    !departmentsListError &&
    departmentsListFromApi.length === 0
  ) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 400,
          gap: 2,
          p: 3,
          textAlign: "center",
        }}
      >
        <DepartmentIcon sx={{ fontSize: 80, color: "action.disabled" }} />
        <Typography variant="h6" color="text.secondary">
          No departments found
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          No active departments are configured for this institute.
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate(departmentsPath)}
          sx={{ mt: 2 }}
        >
          Add Department
        </Button>
      </Box>
    );
  }

  if ((isLoading && !payload) || (isFetching && !payload) || waitingForDepartmentPick) {
    return <DepartmentViewSkeleton />;
  }
  if (!derived) {
    return null;
  }

  const {
    departmentData,
    departmentStats,
    hasRemoteDepartmentData,
  } = derived;

  const departmentsPath = `/${normalizedRole}/departments`;

  if (!hasRemoteDepartmentData) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 400,
          gap: 2,
        }}
      >
        <DepartmentIcon sx={{ fontSize: 80, color: "action.disabled" }} />
        <Typography variant="h6" color="text.secondary">
          No department information found
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Add a department to display its details, faculty, courses, and events here.
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate(departmentsPath)}
          sx={{ mt: 2 }}
        >
          Add Department
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header - title + optional department picker for institute/admin */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", minWidth: 0, flex: 1 }}>
          <Avatar
            sx={{
              width: 80,
              height: 80,
              bgcolor: "primary.main",
              fontSize: "2rem",
              flexShrink: 0,
            }}
          >
            <DepartmentIcon fontSize="large" />
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h5" fontWeight={600} noWrap title={departmentData.name}>
              {departmentData.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Department Code: {withFallback(departmentData.code)}
            </Typography>
          </Box>
        </Box>
        {canPickDepartment && (
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="dept-view-select-label">Department</InputLabel>
            <Select
              labelId="dept-view-select-label"
              label="Department"
              value={selectedDepartmentId}
              onChange={(e) => setSelectedDepartmentId(String(e.target.value))}
              disabled={departmentsListLoading || isFetching}
              endAdornment={
                departmentsListLoading || isFetching ? (
                  <CircularProgress size={18} sx={{ mr: 2 }} />
                ) : null
              }
            >
              {departmentsListLoading ? (
                <MenuItem disabled value="">
                  Loading departments...
                </MenuItem>
              ) : (
                departmentsListFromApi.map((dept) => (
                  <MenuItem key={dept.id} value={String(dept.id)}>
                    {dept.name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        )}
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: "center", py: 2 }}>
              <PeopleIcon color="primary" fontSize="large" />
              <Typography variant="h4" fontWeight="bold" color="primary.main">
                {departmentStats.totalFaculty}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Faculty Members
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: "center", py: 2 }}>
              <SchoolIcon color="secondary" fontSize="large" />
              <Typography variant="h4" fontWeight="bold" color="secondary.main">
                {departmentStats.totalStudents}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Students
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: "center", py: 2 }}>
              <SubjectIcon color="success" fontSize="large" />
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {departmentStats.totalSubjects}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Subjects
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        {/* Placement Rate - commented out
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: "center", py: 2 }}>
              <TrendingIcon color="warning" fontSize="large" />
              <Typography variant="h4" fontWeight="bold" color="warning.main">
                {departmentStats.placementRate}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Placement Rate
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        */}
      </Grid>

      {/* HOD Card */}
      <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Head of Department
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  bgcolor: "primary.main",
                  fontSize: "1.5rem",
                }}
              >
                {departmentData.hodName ? departmentData.hodAvatar : "—"}
              </Avatar>
              <Box>
                <Typography variant="h6">
                  {departmentData.hodName || "Not assigned"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {departmentData.hodDesignation || "—"}
                </Typography>
                <Box sx={{ display: "flex", gap: 2, mt: 1, flexWrap: "wrap" }}>
                  <Typography variant="body2">
                    <EmailIcon
                      fontSize="small"
                      sx={{ verticalAlign: "middle", mr: 0.5 }}
                    />
                    {departmentData.hodEmail || "—"}
                  </Typography>
                  <Typography variant="body2">
                    <PhoneIcon
                      fontSize="small"
                      sx={{ verticalAlign: "middle", mr: 0.5 }}
                    />
                    {departmentData.hodPhone || "—"}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>

      {/* Tabs */}
      <Card>
        <Tabs
          value={tabValue}
          onChange={(e, v) => setTabValue(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label="About" />
          <Tab label="Courses" />
          <Tab label="Events" />
          {/* <Tab label="Achievements" /> */}
          {/* <Tab label="Statistics" /> */}
        </Tabs>

        <CardContent>
          {/* About Tab */}
          {tabValue === 0 && (
            <Box>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                About the Department
              </Typography>
              <Typography variant="body1" paragraph>
                {departmentData.description || "—"}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      color="primary.main"
                      gutterBottom
                    >
                      Vision
                    </Typography>
                    <Typography variant="body2">
                      {departmentData.vision || "—"}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      color="secondary.main"
                      gutterBottom
                    >
                      Mission
                    </Typography>
                    <Typography variant="body2">
                      {departmentData.mission || "—"}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Faculty Tab - commented out
          {tabValue === 1 && (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6" fontWeight="bold">
                  Faculty Members ({facultyMembers.length})
                </Typography>
                <AvatarGroup max={4}>
                  {facultyMembers.map((f) => (
                    <Tooltip key={f.id} title={f.name}>
                      <Avatar>{f.avatar}</Avatar>
                    </Tooltip>
                  ))}
                </AvatarGroup>
              </Box>
              <Grid container spacing={2}>
                {facultyMembers.map((faculty) => (
                  <Grid item xs={12} sm={6} md={4} key={faculty.id}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        display: "flex",
                        gap: 2,
                        alignItems: "center",
                      }}
                    >
                      <Avatar
                        sx={{ bgcolor: "primary.main", width: 48, height: 48 }}
                      >
                        {faculty.avatar}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {faculty.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          {faculty.designation}
                        </Typography>
                        <Chip
                          label={faculty.specialization}
                          size="small"
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
          */}

          {/* Courses Tab - list from /course/all API */}
          {tabValue === 1 && (
            <Box>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Programs Offered
              </Typography>
              {coursesLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    py: 4,
                    minHeight: 200,
                    gap: 2,
                  }}
                >
                  <CircularProgress size={40} />
                  <Typography variant="body2" color="text.secondary">
                    Loading programs...
                  </Typography>
                </Box>
              ) : coursesError ? (
                <Typography color="error" sx={{ py: 2 }}>
                  Failed to load courses. Please try again.
                </Typography>
              ) : (
                <>
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Program Name</TableCell>
                          <TableCell>Code</TableCell>
                          <TableCell>Duration</TableCell>
                          <TableCell>Students</TableCell>
                          <TableCell>Subjects</TableCell>
                          <TableCell>Department</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {coursesListFromApi.map((course) => (
                          <TableRow key={course.id}>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {course.name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip label={course.code} size="small" />
                            </TableCell>
                            <TableCell>{course.duration}</TableCell>
                            <TableCell>{course.studentsCount}</TableCell>
                            <TableCell>{course.subjectsCount}</TableCell>
                            <TableCell>{course.department || "—"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={coursesPagination.total}
                    page={coursesPage}
                    onPageChange={(_, newPage) => setCoursesPage(newPage)}
                    rowsPerPage={coursesRowsPerPage}
                    onRowsPerPageChange={(e) => {
                      setCoursesRowsPerPage(parseInt(e.target.value, 10));
                      setCoursesPage(0);
                    }}
                    rowsPerPageOptions={PAGINATION_PAGE_SIZE_OPTIONS}
                    labelRowsPerPage="Rows per page:"
                  />
                </>
              )}
            </Box>
          )}

          {/* Events Tab - institute events (scoped to selected institute) */}
          {tabValue === 2 && (
            <Box>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Events
              </Typography>
              {eventsLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    py: 4,
                    minHeight: 200,
                    gap: 2,
                  }}
                >
                  <CircularProgress size={40} />
                  <Typography variant="body2" color="text.secondary">
                    Loading events...
                  </Typography>
                </Box>
              ) : eventsError ? (
                <Typography color="error" sx={{ py: 2 }}>
                  Failed to load events. Please try again.
                </Typography>
              ) : (
                <>
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Event</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Venue</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {eventsListFromApi.map((event) => (
                          <TableRow key={event.id}>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {event.name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {event.date
                                ? displayDate(event.date)
                                : "—"}
                            </TableCell>
                            <TableCell>
                              <Chip label={event.type} size="small" />
                            </TableCell>
                            <TableCell>{event.location}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={eventsPagination.total}
                    page={eventsPage}
                    onPageChange={(_, newPage) => setEventsPage(newPage)}
                    rowsPerPage={eventsRowsPerPage}
                    onRowsPerPageChange={(e) => {
                      setEventsRowsPerPage(parseInt(e.target.value, 10));
                      setEventsPage(0);
                    }}
                    rowsPerPageOptions={PAGINATION_PAGE_SIZE_OPTIONS}
                    labelRowsPerPage="Rows per page:"
                  />
                </>
              )}
            </Box>
          )}

          {/* Achievements Tab - commented out
          {tabValue === 4 && (
            <Box>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Achievements & Recognition
              </Typography>
              <Grid container spacing={2}>
                {achievements.map((achievement) => (
                  <Grid item xs={12} sm={6} key={achievement.id}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderLeft: 4,
                        borderLeftColor: "primary.main",
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight="bold">
                        🏆 {achievement.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {achievement.description}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
          */}

          {/* Statistics Tab - commented out
          {tabValue === 5 && (
            <Box>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Department Statistics
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Placement Statistics
                    </Typography>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      <Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 0.5,
                          }}
                        >
                          <Typography variant="body2">
                            Placement Rate
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {departmentStats.placementRate}%
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={departmentStats.placementRate}
                          sx={{ height: 10, borderRadius: 1 }}
                          color="success"
                        />
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Average Package
                          </Typography>
                          <Typography
                            variant="h6"
                            fontWeight="bold"
                            color="primary.main"
                          >
                            {departmentStats.averagePackage}
                          </Typography>
                        </Box>
                        <Divider orientation="vertical" flexItem />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Highest Package
                          </Typography>
                          <Typography
                            variant="h6"
                            fontWeight="bold"
                            color="success.main"
                          >
                            {departmentStats.highestPackage}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Research Output
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Avatar
                        sx={{
                          width: 64,
                          height: 64,
                          bgcolor: "secondary.light",
                        }}
                      >
                        <ChartIcon fontSize="large" color="secondary" />
                      </Avatar>
                      <Box>
                        <Typography
                          variant="h3"
                          fontWeight="bold"
                          color="secondary.main"
                        >
                          {departmentStats.researchPapers}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Research Papers Published
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
          */}
        </CardContent>
      </Card>
    </Box>
  );
}

export default DepartmentView;
